import axios from 'axios';

const api = axios.create({
  baseURL: '',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 unauthorized
    if (error.response?.status === 401) {
      // If on a protected route, redirect to login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API functions
export const authApi = {
  login: (username, password) => api.post('/api/auth/login', { username, password }),
  logout: () => api.post('/api/auth/logout'),
  me: () => api.get('/api/auth/me'),
  updateLanguage: (language) => api.put('/api/auth/language', { language }),
  changePassword: (currentPassword, newPassword) =>
    api.put('/api/auth/change-password', { currentPassword, newPassword }),
};

export const dashboardApi = {
  getSummary: () => api.get('/api/dashboard/summary'),
  getDivisionBreakdown: () => api.get('/api/dashboard/division-breakdown'),
  getWeeklyTrend: () => api.get('/api/dashboard/weekly-trend'),
  getRecentActivity: () => api.get('/api/dashboard/recent-activity'),
};

export const reportsApi = {
  getAll: (params) => api.get('/api/reports', { params }),
  getById: (id) => api.get(`/api/reports/${id}`),
  create: (data) => api.post('/api/reports', data),
  update: (id, data) => api.put(`/api/reports/${id}`, data),
  updateStatus: (id, status, cancellationReason) =>
    api.patch(`/api/reports/${id}/status`, { status, cancellationReason }),
  delete: (id) => api.delete(`/api/reports/${id}`),
  // Project Item No. endpoints
  getProjectItems: (division) => api.get('/api/reports/project-items', { params: { division } }),
  getNextProjectItemNo: (division) => api.get('/api/reports/next-project-item-no', { params: { division } }),
};

export const exportApi = {
  exportReports: (params, format = 'excel') =>
    api.get('/api/export/reports', {
      params: { ...params, format },
      responseType: 'blob',
    }),
  getLink: (date) => api.get('/api/export/link', { params: { date } }),
};

export const settingsApi = {
  get: () => api.get('/api/settings'),
  update: (data) => api.put('/api/settings', data),
  getDayType: (date) => api.get('/api/settings/day-type', { params: { date } }),
  getHolidays: (year) => api.get('/api/settings/holidays', { params: { year } }),
  saveHoliday: (data) => api.post('/api/settings/holidays', data),
  deleteHoliday: (id) => api.delete(`/api/settings/holidays/${id}`),
};

export const announcementsApi = {
  getAll: (params) => api.get('/api/announcements', { params }),
  create: (data) => api.post('/api/announcements', data),
  update: (id, data) => api.put(`/api/announcements/${id}`, data),
  delete: (id) => api.delete(`/api/announcements/${id}`),
};

export const importApi = {
  preview: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.defaultDate) formData.append('defaultDate', options.defaultDate);
    if (options.defaultDayType) formData.append('defaultDayType', options.defaultDayType);

    return api.post('/api/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importExcel: (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.defaultDate) formData.append('defaultDate', options.defaultDate);
    if (options.defaultDayType) formData.append('defaultDayType', options.defaultDayType);

    return api.post('/api/import/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getTemplateInfo: () => api.get('/api/import/template'),
  downloadTemplate: () => api.get('/api/import/template/download', { responseType: 'blob' }),
};

export const lineApi = {
  preview: (date) => api.get('/api/line/preview', { params: { date } }),
  sendDaily: (date, message) => api.post('/api/line/send-daily', { date, message }),
};

export const usersApi = {
  getAll: () => api.get('/api/admin/users'),
  getById: (id) => api.get(`/api/admin/users/${id}`),
  create: (data) => api.post('/api/admin/users', data),
  update: (id, data) => api.put(`/api/admin/users/${id}`, data),
  resetPassword: (id, password) => api.put(`/api/admin/users/${id}/reset-password`, { password }),
  delete: (id) => api.delete(`/api/admin/users/${id}`),
};

export const contractorsApi = {
  getAll: (params) => api.get('/api/admin/contractors', { params }),
  getById: (id) => api.get(`/api/admin/contractors/${id}`),
  create: (data) => api.post('/api/admin/contractors', data),
  update: (id, data) => api.put(`/api/admin/contractors/${id}`, data),
  delete: (id) => api.delete(`/api/admin/contractors/${id}`),
  setActive: (id, isActive) => api.patch(`/api/admin/contractors/${id}/active`, { isActive }),
  getUsage: (id) => api.get(`/api/admin/contractors/${id}/usage`),
};

export const inspectorsApi = {
  getAll: (params) => api.get('/api/admin/inspectors', { params }),
  getById: (id) => api.get(`/api/admin/inspectors/${id}`),
  create: (data) => api.post('/api/admin/inspectors', data),
  update: (id, data) => api.put(`/api/admin/inspectors/${id}`, data),
  delete: (id) => api.delete(`/api/admin/inspectors/${id}`),
  setActive: (id, isActive) => api.patch(`/api/admin/inspectors/${id}/active`, { isActive }),
  getUsage: (id) => api.get(`/api/admin/inspectors/${id}/usage`),
  getCompanies: () => api.get('/api/admin/inspectors/meta/companies'),
};

export const contractorPersonnelApi = {
  getAll: (params) => api.get('/api/admin/contractor-personnel', { params }),
  getById: (id) => api.get(`/api/admin/contractor-personnel/${id}`),
  create: (data) => api.post('/api/admin/contractor-personnel', data),
  update: (id, data) => api.put(`/api/admin/contractor-personnel/${id}`, data),
  delete: (id) => api.delete(`/api/admin/contractor-personnel/${id}`),
  getByContractor: (contractorId) => api.get(`/api/admin/contractor-personnel/by-contractor/${contractorId}`),
};
